import type { EditIssueForm } from '../types'
import type { IssueFormFieldValue, IssueFormMode } from './types'
import { IssueFormField } from './types'
import type { UseIssueFieldConfig } from './use-issue-field-config'

/** Form state key -> the configurable field it renders. */
export const ISSUE_FORM_KEY_TO_FIELD = {
  title: IssueFormField.TITLE,
  category: IssueFormField.CATEGORY,
  subcategory: IssueFormField.SUBCATEGORY,
  severity: IssueFormField.SEVERITY,
  riskAspect: IssueFormField.RISK_ASPECT,
  processChoice: IssueFormField.PROCESS_CHOICE,
  responsibleUser: IssueFormField.RESPONSIBLE_USER,
  caseHandler: IssueFormField.CASE_HANDLER,
  tags: IssueFormField.TAGS,
  placename: IssueFormField.LOCATION_PLACENAME,
  locationDescription: IssueFormField.LOCATION_DESCRIPTION,
  regNr: IssueFormField.VEHICLE_PLATE_NUMBER,
  description: IssueFormField.ISSUE_DESCRIPTION,
  damage: IssueFormField.DAMAGE_DESCRIPTION,
  reason: IssueFormField.REASON,
  immediateAction: IssueFormField.IMMEDIATE_ACTION,
  action: IssueFormField.ACTION,
  suggestedSolution: IssueFormField.SOLUTION_SUGGESTION,
  estimatedCost: IssueFormField.COST_ESTIMATION,
} as const satisfies Partial<Record<keyof EditIssueForm, IssueFormFieldValue>>

export type IssueFormKey = keyof typeof ISSUE_FORM_KEY_TO_FIELD

export type IssueFormValidator = (value: unknown) => string | null

/**
 * Mutation input keys a field owns. Location owns the coordinates too, so
 * hiding it drops the whole place payload rather than half of it.
 */
const FIELD_TO_INPUT_KEYS: Partial<Record<IssueFormFieldValue, string[]>> = {
  [IssueFormField.TITLE]: ['title'],
  [IssueFormField.SUBCATEGORY]: ['subcategory'],
  [IssueFormField.SEVERITY]: ['severity'],
  [IssueFormField.RISK_ASPECT]: ['riskAspect'],
  [IssueFormField.PROCESS_CHOICE]: ['processChoice'],
  [IssueFormField.RESPONSIBLE_USER]: ['responsibleUser'],
  [IssueFormField.CASE_HANDLER]: ['caseHandler'],
  [IssueFormField.TAGS]: ['tags'],
  [IssueFormField.LOCATION_PLACENAME]: [
    'locationPlacename',
    'locationLatitude',
    'locationLongitude',
  ],
  [IssueFormField.LOCATION_DESCRIPTION]: ['locationDescription'],
  [IssueFormField.VEHICLE_PLATE_NUMBER]: ['vehiclePlateNumber'],
  [IssueFormField.ISSUE_DESCRIPTION]: ['issueDescription'],
  [IssueFormField.DAMAGE_DESCRIPTION]: ['damageDescription'],
  [IssueFormField.REASON]: ['reason'],
  [IssueFormField.IMMEDIATE_ACTION]: ['immediateAction'],
  [IssueFormField.ACTION]: ['action'],
  [IssueFormField.SOLUTION_SUGGESTION]: ['solutionSuggestion'],
  [IssueFormField.COST_ESTIMATION]: ['costEstimation'],
}

function isBlank(value: unknown): boolean {
  if (value === null || value === undefined) return false
  if (typeof value === 'string') return value.trim() === ''
  if (Array.isArray(value)) return value.length === 0
  return false
}

export interface IssueValidatorOptions {
  requiredMessage: string
  /**
   * Read lazily: both are derived from queries the view declares *after* it
   * builds its form, and both can flip while the form is open.
   */
  hasRiskAspects: () => boolean
  hasSubcategories: () => boolean
}

/**
 * Validators derived from the company's configuration, so "which fields are
 * mandatory" has one source instead of a hardcoded literal per view.
 *
 * Each validator reads the config at call time (the hook's callbacks go through
 * a ref), so validators captured on the first render still see the config once
 * it loads.
 */
export function buildIssueFormValidators(
  config: UseIssueFieldConfig,
  mode: IssueFormMode,
  options: IssueValidatorOptions
): Record<IssueFormKey, IssueFormValidator> {
  const validators = {} as Record<IssueFormKey, IssueFormValidator>

  for (const [formKey, field] of Object.entries(ISSUE_FORM_KEY_TO_FIELD) as [
    IssueFormKey,
    IssueFormFieldValue,
  ][]) {
    validators[formKey] = value => {
      if (!config.isVisible(field, mode)) return null
      if (!config.isRequired(field, mode)) return null
      if (field === IssueFormField.RISK_ASPECT && !options.hasRiskAspects())
        return null
      if (field === IssueFormField.SUBCATEGORY && !options.hasSubcategories())
        return null

      const empty =
        value === null || value === undefined ? true : isBlank(value)
      return empty ? options.requiredMessage : null
    }
  }

  return validators
}

/** Drop the payload of fields the company has hidden for this mode. */
export function omitHiddenIssueInput<T extends object>(
  config: UseIssueFieldConfig,
  mode: IssueFormMode,
  input: T
): T {
  const hidden = new Set<string>()
  for (const [field, keys] of Object.entries(FIELD_TO_INPUT_KEYS)) {
    if (config.isVisible(field as IssueFormFieldValue, mode)) continue
    for (const key of keys) hidden.add(key)
  }

  if (hidden.size === 0) return input

  return Object.fromEntries(
    Object.entries(input).filter(([key]) => !hidden.has(key))
  ) as T
}

/**
 * Which fields each `DetailsSection` card renders.
 *
 * The backend's `section` is not usable for this: `attachments` is filed under
 * RESOLUTION there but renders as its own card in the views, so a
 * data-derived membership would keep the Resolution card alive for it. The
 * lists below describe the *cards*, and `form-fields.test.ts` asserts they
 * partition every configurable field so a new field cannot be forgotten.
 */
export const ISSUE_DETAILS_SECTION_FIELDS = {
  LOCATION: [
    IssueFormField.LOCATION_PLACENAME,
    IssueFormField.VEHICLE_PLATE_NUMBER,
    IssueFormField.LOCATION_DESCRIPTION,
  ],
  EVENT: [
    IssueFormField.ISSUE_DESCRIPTION,
    IssueFormField.DAMAGE_DESCRIPTION,
    IssueFormField.REASON,
  ],
  RESOLUTION: [
    IssueFormField.IMMEDIATE_ACTION,
    IssueFormField.ACTION,
    IssueFormField.SOLUTION_SUGGESTION,
  ],
  COST: [IssueFormField.COST_ESTIMATION],
} as const satisfies Record<string, readonly IssueFormFieldValue[]>

export type IssueDetailsSection = keyof typeof ISSUE_DETAILS_SECTION_FIELDS

/**
 * Fields that render outside those cards — the metadata rail, the title card
 * and the attachments card. Listed so the partition test can see them.
 */
export const ISSUE_STANDALONE_FIELDS: readonly IssueFormFieldValue[] = [
  IssueFormField.CATEGORY,
  IssueFormField.SUBCATEGORY,
  IssueFormField.TITLE,
  IssueFormField.SEVERITY,
  IssueFormField.RISK_ASPECT,
  IssueFormField.PROCESS_CHOICE,
  IssueFormField.RESPONSIBLE_USER,
  IssueFormField.CASE_HANDLER,
  IssueFormField.TAGS,
  IssueFormField.TIME_OF_ISSUE,
  IssueFormField.ATTACHMENTS,
]

/**
 * True when a section card has at least one field left to show. Cards whose
 * only remaining content is the close-out fields (final solution, actual
 * cost) pass `alsoVisible` — those render only on a closed issue.
 */
export function isDetailsSectionVisible(
  config: UseIssueFieldConfig,
  mode: IssueFormMode,
  section: IssueDetailsSection,
  alsoVisible = false
): boolean {
  if (alsoVisible) return true
  return ISSUE_DETAILS_SECTION_FIELDS[section].some(field =>
    config.isVisible(field, mode)
  )
}
